/**
 * The app's vocabulary — one term per concept, defined once.
 *
 * A beta tester found the same feature carrying five names, the word "check-in"
 * covering three unrelated things, and "Checked in today" meaning two different
 * things on one screen. The cause was not carelessness in any single component:
 * it was that every component wrote its own string literal, so nothing held the
 * app to one word.
 *
 * `MEASUREMENT_METHOD_LABELS` (components/weight/types.ts) is the pattern this
 * module generalises. Import the term; never re-type it. See DECISIONS.md §DS-1
 * for the decision table and the reasoning behind each choice.
 *
 * The words themselves live in the locale file under `vocabulary.*`; every term
 * here is a getter (or a function) so it is looked up when read, not at import.
 */

import i18n from '../i18n';
import { capitalizeFirst, possessive } from './pronouns';

/**
 * The daily symptom log. It owns the word **log** — the route is `/daily-log`,
 * the table is `daily_logs`, and the history list has always been "Log history".
 * It does NOT say "check-in": that word belongs to the weekly QoL rating alone.
 */
export const DAILY_LOG = {
  /** Sidebar and bottom-nav label. */
  get nav() { return i18n.t('vocabulary.dailyLog.nav'); },
  /** Page heading and dashboard card title. */
  get title() { return i18n.t('vocabulary.dailyLog.title'); },
  /** The entry form's own heading, and the caregiver card. */
  get today() { return i18n.t('vocabulary.dailyLog.today'); },
  /** The list of past days. */
  get history() { return i18n.t('vocabulary.dailyLog.history'); },
  /** Primary button when today has no entry yet. */
  get save() { return i18n.t('vocabulary.dailyLog.save'); },
  /** Primary button when today already has an entry. */
  get update() { return i18n.t('vocabulary.dailyLog.update'); },
  /** Dashboard link when today already has an entry. */
  get view() { return i18n.t('vocabulary.dailyLog.view'); },
  /** Dashboard link when it does not. */
  get start() { return i18n.t('vocabulary.dailyLog.start'); },
  /** Same action on a button, where an arrow would be noise. The "Getting started"
   *  card used to label this "Start check-in", which put a second referent for
   *  "check-in" on the one screen the weekly QoL card also calls a check-in. */
  get startAction() { return i18n.t('vocabulary.dailyLog.startAction'); },
  /** First-time invitation on the "Getting started" checklist — the daily sibling
   *  of WEEKLY_CHECKIN.first. It read "Log your first daily check-in". */
  get first() { return i18n.t('vocabulary.dailyLog.first'); },
  /** Dashboard status when today has an entry. */
  get loggedToday() { return i18n.t('vocabulary.dailyLog.loggedToday'); },
  /** Dashboard status when it does not. */
  get notLoggedToday() { return i18n.t('vocabulary.dailyLog.notLoggedToday'); },
  /** Confirmation heading after a save. */
  get saved() { return i18n.t('vocabulary.dailyLog.saved'); },
} as const;

/**
 * The weekly quality-of-life rating — the ONE concept that keeps "check-in".
 * "Rating" alone names the number, not the act of giving it, which is why this
 * instrument and not the other two kept the word.
 */
export const WEEKLY_CHECKIN = {
  /** Tab, card title, section heading. */
  get title() { return i18n.t('vocabulary.weeklyCheckin.title'); },
  /** Dashboard status when the week's rating exists. Takes the number of days
   *  since it was given: 0 → "today", 1 → "yesterday", n → "n days ago". It took
   *  the "when" phrase pre-built, which assembled the sentence from fragments. */
  checkedIn: (daysSince: number): string =>
    daysSince === 0
      ? i18n.t('vocabulary.weeklyCheckin.checkedInToday')
      : daysSince === 1
        ? i18n.t('vocabulary.weeklyCheckin.checkedInYesterday')
        : i18n.t('vocabulary.weeklyCheckin.checkedInDaysAgo', { days: daysSince }),
  /** Dashboard line for when the next one is due. */
  nextDue: (days: number): string => i18n.t('vocabulary.weeklyCheckin.nextDue', { count: days }),
  /** First-time invitation. */
  get first() { return i18n.t('vocabulary.weeklyCheckin.first'); },
  /** Primary button. */
  get save() { return i18n.t('vocabulary.weeklyCheckin.save'); },
  /** Dashboard link when this week's rating can still be revised. */
  get update() { return i18n.t('vocabulary.weeklyCheckin.update'); },
} as const;

/**
 * The monthly quality-of-life questionnaire. It owns **assessment** — the field
 * is `is_monthly_deep` and the tab has always read "Full assessment". "Monthly"
 * leads because how often it is asked was the one thing the old label omitted.
 */
export const MONTHLY_ASSESSMENT = {
  /** Tab and card title, where the monthly cadence is already clear from context. */
  get short() { return i18n.t('vocabulary.monthlyAssessment.short'); },
  /** Anywhere the cadence is not already on screen. */
  get title() { return i18n.t('vocabulary.monthlyAssessment.title'); },
  /** Primary button. */
  get save() { return i18n.t('vocabulary.monthlyAssessment.save'); },
  /** Link that opens it. */
  get start() { return i18n.t('vocabulary.monthlyAssessment.start'); },
} as const;

/**
 * Care Pass vocabulary. The person is a **caregiver** — not a sitter (which
 * wrongly narrows a spouse or a neighbour to paid help), not a delegate (a
 * database word). Ending a pass is **revoke**, and the state it leaves behind is
 * **revoked**, matching the stored `care_pass.status` value; the UI used to show
 * that same state as "Canceled".
 */
export const CARE_PASS = {
  /** The feature. */
  get title() { return i18n.t('vocabulary.carePass.title'); },
  /** The person, mid-sentence. */
  get person() { return i18n.t('vocabulary.carePass.person'); },
  /** The person, sentence-initial or as a label. */
  get Person() { return i18n.t('vocabulary.carePass.personCapitalized'); },
  /** Plural, mid-sentence. */
  get people() { return i18n.t('vocabulary.carePass.people'); },
  /** The action. */
  get revoke() { return i18n.t('vocabulary.carePass.revoke'); },
  /** The action, spelled out on a confirmation button. */
  get revokeConfirm() { return i18n.t('vocabulary.carePass.revokeConfirm'); },
  /** The resulting status. */
  get revoked() { return i18n.t('vocabulary.carePass.revoked'); },
  /** Confirmation dialog. */
  get revokeTitle() { return i18n.t('vocabulary.carePass.revokeTitle'); },
  get revokeMessage() { return i18n.t('vocabulary.carePass.revokeMessage'); },
} as const;

/**
 * Subcutaneous fluids. One spelling, everywhere — nav, headings, mid-sentence —
 * in two cases: `label` stands alone (nav, a heading, a chip, a line of its own)
 * and opens with a capital; `inSentence` is the term as the catalogue writes it,
 * for interpolation. English writes "Sub-Q fluids" either way; Portuguese writes
 * "fluidos subcutâneos" mid-sentence and "Fluidos subcutâneos" standing alone.
 */
export const SUBQ_FLUIDS = {
  get label() { return capitalizeFirst(i18n.t('vocabulary.subqFluids')); },
  get inSentence() { return i18n.t('vocabulary.subqFluids'); },
} as const;

/**
 * Data that was never recorded — a state that is now clinically distinct from an
 * answered "No", and therefore has to read identically on every surface a vet
 * might see. Sentence case, always.
 */
export const ABSENT = {
  /** The default, and the only form used outside a scoped report section. */
  get label() { return i18n.t('vocabulary.absent.label'); },
  /** Only where a date range genuinely scopes the claim (vet prep sheet sections). */
  get inPeriod() { return i18n.t('vocabulary.absent.inPeriod'); },
} as const;

/**
 * The absent marker WITH the thing that is absent, for the vet prep sheet.
 *
 * In the app a bare "Not recorded" sits inside a screen the owner can interrogate:
 * they know what they were looking at, and anything unclear is one tap away. The
 * vet sheet is a printout. A reader who meets a bare "Not recorded." under
 * "Current medications" cannot tell whether the cat is on nothing or whether the
 * owner never entered anything, and those two lead to different questions in the
 * consult room — so on that document the line has to name its subject.
 *
 * The marker itself is still the single unified one (DECISIONS.md §DS-1); this only
 * gives it back a subject, as the predicate of a sentence that has one.
 *
 *   absentLine('Active medications')                   → 'Active medications: not recorded.'
 *   absentLine('Sub-Q fluid sessions', 'period')       → 'Sub-Q fluid sessions: not recorded in this period.'
 */
export function absentLine(subject: string, scope: 'always' | 'period' = 'always'): string {
  return scope === 'period'
    ? i18n.t('vocabulary.absent.linePeriod', { subject })
    : i18n.t('vocabulary.absent.line', { subject });
}

/**
 * The three observation questions — vomiting, bowel movement, urination — and the
 * FOUR states each of them can be in.
 *
 * This is the one place `ABSENT.label` must not be used as a catch-all. Everywhere
 * else "Not recorded" covers every kind of missing data, and that is fine because
 * nothing turns on the difference. Here it does: an owner who answered "No", an
 * owner who answered "Don't know", and an owner who was never asked are making three
 * different clinical statements, and a vet reading the prep sheet has to be able to
 * tell them apart. So each state gets its own words, and they are the words the vet
 * sheet already prints — "0 days with vomiting, none reported on 1 day, not observed
 * on 2, no record on 3" — so the owner reading the chips, the owner reading Log
 * History and the vet reading the sheet all meet the same vocabulary.
 *
 * Note "Don't know" is the BUTTON, and "not observed" is the RECORD it writes. That
 * split is deliberate and already shipped: the control asks the owner a question in
 * their own words, and the record states what is now known about the day.
 */
export const OBSERVATION = {
  /** An answered "No" — mid-sentence, as the vet sheet prints it. */
  get noneReported() { return i18n.t('vocabulary.observation.noneReported'); },
  /** An answered "No", sentence-initial or as a standalone value. */
  get NoneReported() { return i18n.t('vocabulary.observation.noneReportedCapitalized'); },
  /** An explicit "Don't know", mid-sentence. */
  get notObserved() { return i18n.t('vocabulary.observation.notObserved'); },
  /** An explicit "Don't know", sentence-initial or as a standalone value. */
  get NotObserved() { return i18n.t('vocabulary.observation.notObservedCapitalized'); },
  /** No answer at all, mid-sentence. */
  get noRecord() { return i18n.t('vocabulary.observation.noRecord'); },
  /** No answer at all, sentence-initial or as a standalone value. */
  get NoRecord() { return i18n.t('vocabulary.observation.noRecordCapitalized'); },
  /**
   * The hint under a question nobody has answered yet. It used to add "— that's
   * different from 'No'", which was doing real work while blank was the only way to
   * say "I don't know". Every question now carries a visible "Don't know" button, so
   * the clause explains a distinction the controls already make.
   */
  get notAnsweredYet() { return i18n.t('vocabulary.observation.notAnsweredYet'); },
} as const;

/**
 * The Settings switch for the dashboard's daily tip line. Named for what it does:
 * it has nothing to do with Care Pass caregivers, and the old "Note for
 * caregivers" collided head-on with them.
 */
export const DASHBOARD_NOTE = {
  get title() { return i18n.t('vocabulary.dashboardNote.title'); },
  get description() { return i18n.t('vocabulary.dashboardNote.description'); },
} as const;

/**
 * Archiving a cat's record (F-08) — the lifecycle state that stops every scheduled
 * prompt, reminder and alert for one cat in a single action.
 *
 * The register is fixed and deliberate. A meaningful number of the owners who reach
 * this control are reaching it because their cat has died. The copy therefore says
 * what will happen and stops: it does not ask why, it does not offer condolences,
 * and it never uses a euphemism for the thing it will not name. Every word here was
 * approved by the product owner; change it only with him.
 */
export const ARCHIVE = {
  /** The control on /cat-profile. */
  get action() { return i18n.t('vocabulary.archive.action'); },
  /** The section heading above it, before and after archiving. */
  get sectionTitle() { return i18n.t('vocabulary.archive.sectionTitle'); },
  get archivedSectionTitle() { return i18n.t('vocabulary.archive.archivedSectionTitle'); },
  /** What the control does, shown beside it before anything is confirmed. */
  get sectionDescription() { return i18n.t('vocabulary.archive.sectionDescription'); },
  /** Confirmation dialog, before archiving. */
  confirmTitle: (catName: string, catSex?: 'male' | 'female' | null) =>
    i18n.t('vocabulary.archive.confirmTitle', { catNamePossessive: possessive(catName, catSex) }),
  confirmMessage: (catName: string) => i18n.t('vocabulary.archive.confirmMessage', { catName }),
  /**
   * Appended to the confirmation ONLY when the cat has care passes a caregiver
   * could still use — the owners who have never issued one are told nothing about
   * them. One clause, in the same register as the rest: what will happen, and stop.
   * It does not go on to explain that restoring will not bring the pass back.
   */
  confirmCarePasses: (count: number) => i18n.t('vocabulary.archive.confirmCarePasses', { count }),

  /** The state shown on /cat-profile once archived. Takes a formatted date. */
  archivedOn: (date: string) => i18n.t('vocabulary.archive.archivedOn', { date }),
  get archivedDetail() { return i18n.t('vocabulary.archive.archivedDetail'); },
  /** The chip beside an archived cat's name, on the dashboard and in the selector. */
  get chip() { return i18n.t('vocabulary.archive.chip'); },

  /** Restoring. */
  get restoreAction() { return i18n.t('vocabulary.archive.restoreAction'); },
  restoreTitle: (catName: string, catSex?: 'male' | 'female' | null) =>
    i18n.t('vocabulary.archive.restoreTitle', { catNamePossessive: possessive(catName, catSex) }),
  restoreMessage: (catName: string) => i18n.t('vocabulary.archive.restoreMessage', { catName }),

  /** What the dashboard opens on for an archived cat, in place of "How is X today?". */
  get dashboardNotice() { return i18n.t('vocabulary.archive.dashboardNotice'); },

  /** Failure copy, in the app's usual register for an action that did not take. */
  get archiveFailedTitle() { return i18n.t('vocabulary.archive.archiveFailedTitle'); },
  get restoreFailedTitle() { return i18n.t('vocabulary.archive.restoreFailedTitle'); },
  get failedMessage() { return i18n.t('vocabulary.archive.failedMessage'); },
} as const;
